import { PromiseCache } from '@zajno/common/structures/promiseCache';
import { assert } from 'common/assert.ts';
import { createLogger } from 'common/logger';
import { SupabaseClient, wrapSupabase } from '../types/client.ts';
import { UserProfile } from '../types/models/user.ts';
import { NumberFilter } from './_helpers.ts';

const logger = createLogger('[UsersRepository]');


export type UsersFilters = {
    premium_level?: NumberFilter;

    pages?: {
        offset?: number;
        limit?: number;
    };
};

enum CacheTypes {
    Email = 1 << 1,
    Id = 1 << 2,

    All = Email | Id,
}

export class UsersRepository {

    // NOT OBSERVABLE
    // since we don't want mobx to work on the backend
    // workaround: extend this class and override PromiseCacheClass getter
    private _cacheById: PromiseCache<UserProfile | null | undefined> | null = null;
    private _cacheByEmail: PromiseCache<UserProfile | null | undefined> | null = null;

    constructor(protected readonly client: SupabaseClient) { }

    public useCaching(use: boolean) {
        this._cacheById = use
            ? new this.PromiseCacheClass(id => this._getUser(id))
            : null;
        this._cacheByEmail = use
            ? new this.PromiseCacheClass(email => this._getUserByEmail(email))
            : null;
        return this;
    }

    protected get PromiseCacheClass() { return PromiseCache; }

    public getUserDeferred(id: string) {
        assert(!!this._cacheById, 'UserRepository.getUserDeferred: caching is not enabled');
        assert(!!id, 'UserRepository.getUserDeferred: id is required');
        return this._cacheById.getDeferred(id);
    }

    public async getUser(id: string, allowCache = true): Promise<UserProfile | null | undefined> {
        assert(!!id, 'UserRepository.getUser: id is required');

        if (!allowCache || !this._cacheById) {
            return this._getUser(id);
        }

        const user = await this._cacheById.get(id);
        this.cacheUser(user, CacheTypes.Email);
        return user;
    }

    public async getUserByEmail(email: string, allowCache = true): Promise<UserProfile | null | undefined> {
        assert(!!email, 'UserRepository.getUserByEmail: email is required');

        if (!allowCache || !this._cacheByEmail) {
            return this._getUserByEmail(email);
        }

        const user = await this._cacheByEmail.get(email);
        this.cacheUser(user, CacheTypes.Id);
        return user;
    }

    private _getUser(id: string): Promise<UserProfile | null | undefined> {
        return wrapSupabase(this.client.from('users').select().eq('id', id).maybeSingle());
    }

    private _getUserByEmail(email: string): Promise<UserProfile | null | undefined> {
        return wrapSupabase(this.client.from('users').select().eq('email', email).maybeSingle());
    }

    public async getUsers(args?: UsersFilters) {
        let q = this.client
            .from('users')
            .select<'*', UserProfile>()
            .order('created_at', { ascending: false });

        if (args?.premium_level) {
            q = NumberFilter.populate(q, args.premium_level, 'premium_level');
        }

        if (args?.pages?.offset != null) {
            q = q.range(args.pages.offset, args.pages.offset + ((Math.max(1, args.pages.limit ?? 100)) - 1));
        } else if (args?.pages?.limit) {
            q = q.limit(args.pages.limit);
        }

        const results = await wrapSupabase(q);
        results?.forEach(r => this.cacheUser(r));
        return results;
    }

    public async setPremiumLevel(userId: string, premiumLevel: number) {
        assert(!!userId, 'UserRepository.setPremiumLevel: userId is required');
        assert(premiumLevel != null, 'UserRepository.setPremiumLevel: premiumLevel is required');

        const result = await wrapSupabase(
            this.client.from('users')
                .update({ premium_level: premiumLevel })
                .eq('id', userId)
                .select<'*', UserProfile>()
                .single<UserProfile>(),
        );

        this.cacheUser(result);

        return result;
    }

    public subscribeToUser(id: string, callback: (user: UserProfile) => void) {
        assert(!!id, 'UserRepository.subscribeToUser: id is required');
        assert(!!callback, 'UserRepository.subscribeToUser: callback is required');

        const result = this.client
            .channel('table-db-changes')
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'users',
                    filter: `id=eq.${id}`,
                },
                payload => {
                    const user = payload?.new as UserProfile;
                    this.cacheUser(user);
                    callback(user);
                },
            )
            .subscribe((status, err) => {
                if (status === 'SUBSCRIBED') {
                    logger.log('Subscribed to user changes, id =', id);
                }
                if (err) {
                    logger.error('Failed to subscribe to user changes', id, err);
                }
            });

        return () => {
            result.unsubscribe()
                .then(() => { /* no-op */ })
                .catch(err => {
                    logger.error('Failed to unsubscribe from user changes', id, err);
                });
        };
    }

    public async updateUserName(userId: string, name: string) {
        assert(!!userId, 'UserRepository.updatePersonalInfo: userId is required');
        assert(!!name, 'UserRepository.updatePersonalInfo: name is required');

        const result = await wrapSupabase(
            this.client.rpc(
                'update_user_profile_name',
                {
                    _user_id: userId,
                    _name: name,
                },
            ),
        ) as UserProfile;

        this.cacheUser(result);

        return result;
    }

    public async updateUserPhone(userId: string, phone: string) {
        assert(!!userId, 'UserRepository.updateUserPhone: userId is required');
        assert(!!phone, 'UserRepository.updateUserPhone: phone is required');

        const result = await wrapSupabase(
            this.client.rpc(
                'update_user_profile_phone',
                {
                    _user_id: userId,
                    _phone: phone,
                },
            ),
        ) as UserProfile;

        this.cacheUser(result);

        return result;
    }

    private cacheUser(user: UserProfile | null | undefined, caches: CacheTypes = CacheTypes.All) {
        if (!user) {
            return;
        }

        const isCache = (c: CacheTypes) => (caches & c) === c;

        if (isCache(CacheTypes.Email) && this._cacheByEmail) {
            const email = UserProfile.getEmail(user);
            if (email) {
                this._cacheByEmail.updateValueDirectly(email, user);
            }
        }

        if (isCache(CacheTypes.Id) && this._cacheById) {
            this._cacheById.updateValueDirectly(user.id, user);
        }
    }
}

export class ServerUsersRepository extends UsersRepository {

    public async updateStripeCustomerId(userId: string, customerId: string) {
        assert(!!userId, 'UserRepository.updatePersonalInfo: userId is required');
        assert(!!customerId, 'UserRepository.updatePersonalInfo: name is required');

        const result = await wrapSupabase(
            this.client.rpc(
                'update_user_stripe_customer_id',
                {
                    _user_id: userId,
                    _customer_id: customerId,
                },
            ),
        ) as UserProfile;

        return result;
    }
}
